import * as THREE from "three";
import { instantiate, loadModel, type ModelInstance } from "../assets/loader.js";
import type { BaseState } from "./api.js";
import { CELL, cellToWorld } from "./grid.js";
import { RigidCrowd } from "./RigidCrowd.js";

type Template = Awaited<ReturnType<typeof loadModel>>;
type Placed = BaseState["loop"] extends infer L | undefined ? (L extends { decorations: (infer D)[] } ? D : never) : never;

/** Fit an imported garden inside its authoritative one-cell footprint without changing shared geometry. */
export function fitGarden(object: THREE.Object3D): THREE.Group {
  return fitDecoration(object, 1, 1, 0);
}

/** Fit an imported decoration inside its w×h footprint (rotated in quarter turns) without changing shared geometry (D70). */
export function fitDecoration(object: THREE.Object3D, w: number, h: number, rot: number): THREE.Group {
  const fitted = new THREE.Group(); fitted.add(object);
  const bounds = new THREE.Box3().setFromObject(object), size = bounds.getSize(new THREE.Vector3()), centre = bounds.getCenter(new THREE.Vector3());
  // The model's own long side follows the footprint's long side; the footprint arrives already rotated.
  const modelW = Math.max(size.x, 0.001), modelD = Math.max(size.z, 0.001);
  const scale = Math.min((CELL * w * 0.84) / modelW, (CELL * h * 0.84) / modelD);
  object.position.sub(new THREE.Vector3(centre.x, bounds.min.y, centre.z));
  fitted.scale.setScalar(scale);
  fitted.rotation.y = -(rot * Math.PI) / 180;
  return fitted;
}

/** Authoritative, removable decorations of every kind; geometry is instanced per model across all placed copies. */
export class ColonyDecorations {
  readonly group = new THREE.Group();
  private state: BaseState | null = null;
  private signature = "";
  private models: ModelInstance[] = [];
  private crowd: RigidCrowd | null = null;
  private disposed = false;
  private readonly templates = new Map<string, Template>();
  private readonly loading = new Set<string>();
  /** Placed decoration ids in actor order: an instanced hit's index maps back to the decoration. */
  private ids: string[] = [];

  private ensure(modelId: string): void {
    if (this.templates.has(modelId) || this.loading.has(modelId)) return;
    this.loading.add(modelId);
    void loadModel(modelId).then((template) => {
      this.loading.delete(modelId);
      if (this.disposed) return;
      this.templates.set(modelId, template);
      if (this.state) this.rebuild(this.state);
    }).catch((error: unknown) => {
      this.loading.delete(modelId);
      if (!this.disposed) console.error(`Could not load decoration ${modelId}`, error);
    });
  }

  update(state: BaseState): void {
    this.state = state;
    const rows = state.loop?.decorations ?? [];
    for (const row of rows) this.ensure(row.modelId);
    const signature = `${state.grid.size}|${rows.map((row) => `${row.id}:${row.modelId}:${row.x},${row.y},${row.rot},${row.w}x${row.h}`).join(";")}|${[...this.templates.keys()].sort().join(",")}`;
    if (this.signature === signature) return;
    this.signature = signature;
    this.rebuild(state);
  }

  /** The decoration under a ray, if any (instanced hits carry the actor index). */
  pick(raycaster: THREE.Raycaster): string | null {
    if (!this.crowd) return null;
    const hit = raycaster.intersectObjects([this.crowd.group], true)[0];
    if (!hit || hit.instanceId === undefined) return null;
    return this.ids[hit.instanceId] ?? null;
  }

  private clear(): void {
    for (const model of this.models) model.mixer.stopAllAction();
    this.models = [];
    this.ids = [];
    this.crowd?.dispose(); this.crowd = null; this.group.clear();
  }

  private rebuild(state: BaseState): void {
    this.clear();
    const rows = (state.loop?.decorations ?? []).filter((row) => this.templates.has(row.modelId));
    const actors = rows.map((row: Placed) => {
      const model = instantiate(this.templates.get(row.modelId)!); this.models.push(model);
      const actor = fitDecoration(model.object, row.w, row.h, row.rot), point = cellToWorld(row.x, row.y, row.w, row.h, state.grid.size);
      actor.position.set(point.wx, 0.015, point.wz);
      this.ids.push(row.id);
      return actor;
    });
    if (actors.length) { this.crowd = new RigidCrowd(actors); this.group.add(this.crowd.group); }
  }

  dispose(): void { this.disposed = true; this.state = null; this.clear(); }
}
